import { Calendar, MapPin, Printer, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useLanguage } from "@/context/LanguageContext";

const LOGO_URL = "/images/ym6gev0b_AE8ECFCA-9BBA-44D9-A534-DED3210EC5A4.png";

function formatDateRange(start, end) {
  if (!start) return "";
  const opts = { month: "long", day: "numeric", year: "numeric" };
  const s = new Date(start + "T00:00:00").toLocaleDateString("en-US", opts);
  if (!end || end === start) return s;
  const e = new Date(end + "T00:00:00").toLocaleDateString("en-US", opts);
  return `${s} – ${e}`;
}

// Open a fresh browser window with a certificate-style printable HTML doc and
// trigger window.print(). Keeping this in its own window means our app's
// styles don't bleed in and our print CSS can be self-contained.
function openCertificatePrintWindow({ report, registration, t, logoOrigin }) {
  const win = window.open("", "_blank", "width=900,height=1100");
  if (!win) {
    alert("Please allow popups to print this report.");
    return;
  }

  const fullLogoUrl = logoOrigin + LOGO_URL;
  const dateRange = formatDateRange(registration?.camp_start_date, registration?.camp_end_date);
  const time = registration?.camp_time || "";
  const location = registration?.camp_location || "Miramichi, New Brunswick";
  const sentDate = new Date(report.sent_at).toLocaleDateString("en-US", {
    year: "numeric", month: "long", day: "numeric",
  });

  const sec = (label, value) => `
    <section class="report-section">
      <h2>${label}</h2>
      <p>${(value || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</p>
    </section>
  `;

  win.document.open();
  win.document.write(`<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>Progress Report — ${report.participant_name}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Allura&display=swap" rel="stylesheet">
<style>
  /* Letter portrait, half-inch margins. Sized to fit on a single page even
     with longer reports (single-column section stack with tight rhythm). */
  @page { size: letter portrait; margin: 0.4in; }
  html, body { height: 100%; }
  * { box-sizing: border-box; }
  body {
    margin: 0;
    background: #fdfaf2;
    color: #1a1a1a;
    font-family: Georgia, "Times New Roman", serif;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .certificate {
    width: 7.7in;
    height: 10.2in;
    margin: 16px auto;
    padding: 32px 40px;
    background: #fdfaf2;
    border: 2px solid #1a1a1a;
    box-shadow: 0 0 0 4px #fdfaf2, 0 0 0 6px #d4a84b;
    position: relative;
    display: flex;
    flex-direction: column;
  }
  .corner {
    position: absolute;
    width: 24px;
    height: 24px;
    border: 2px solid #d4a84b;
  }
  .corner.tl { top: 10px; left: 10px; border-right: 0; border-bottom: 0; }
  .corner.tr { top: 10px; right: 10px; border-left: 0; border-bottom: 0; }
  .corner.bl { bottom: 10px; left: 10px; border-right: 0; border-top: 0; }
  .corner.br { bottom: 10px; right: 10px; border-left: 0; border-top: 0; }

  .header { text-align: center; margin-bottom: 14px; }
  .header img { height: 120px; width: auto; }
  .academy {
    font-family: "Helvetica Neue", Arial, sans-serif;
    letter-spacing: 6px;
    font-size: 12px;
    color: #6b5b2b;
    text-transform: uppercase;
    margin: 12px 0 4px;
  }
  .doc-title {
    font-size: 26px;
    color: #1a1a1a;
    margin: 0;
    font-weight: 400;
    font-style: italic;
  }

  .player-line {
    text-align: center;
    margin: 16px 0 18px;
    border-top: 1px solid #d4a84b;
    border-bottom: 1px solid #d4a84b;
    padding: 14px 0;
  }
  .player-name {
    font-size: 34px;
    font-weight: 700;
    letter-spacing: 1px;
    color: #1a1a1a;
    margin: 0 0 6px;
  }
  .player-meta {
    color: #555;
    font-size: 12.5px;
    margin: 0;
    line-height: 1.55;
  }
  .player-meta strong { color: #c41e3a; font-weight: 600; }

  /* The 4 sections share the remaining vertical space evenly. Each section
     stretches to fill its slot with padded breathing room, so a short report
     fills the page rather than huddling at the top. */
  .stack {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 10px;
    overflow: hidden;
  }
  .report-section {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 4px 0;
  }
  .report-section h2 {
    font-family: "Helvetica Neue", Arial, sans-serif;
    font-size: 12px;
    letter-spacing: 3px;
    text-transform: uppercase;
    color: #6b5b2b;
    border-bottom: 1px solid #d4a84b;
    padding-bottom: 4px;
    margin: 0 0 8px;
  }
  .report-section p {
    margin: 0;
    line-height: 1.6;
    color: #1a1a1a;
    white-space: pre-wrap;
    font-size: 12.5pt;
    overflow: hidden;
  }

  .footer-row {
    margin-top: 14px;
    padding-top: 10px;
    border-top: 1px solid #d4a84b;
    display: flex;
    justify-content: space-between;
    align-items: end;
    gap: 24px;
    font-size: 11px;
  }
  .signature-mark {
    font-family: "Allura", "Brush Script MT", cursive;
    font-size: 38px;
    color: #1a3a6e;       /* dark ink-blue, like a real pen */
    line-height: 1;
    margin: 0 0 -6px;
    letter-spacing: 1px;
    /* Hand-drawn underline so it looks scribbled, not centered */
    border-bottom: 1px solid #1a1a1a;
    padding-bottom: 6px;
    display: inline-block;
    transform: rotate(-2deg) translateX(2px);
    transform-origin: bottom left;
  }
  .sig-name { font-weight: 600; color: #1a1a1a; margin-top: 8px; }
  .sig-role { color: #555; }
  .academy-foot {
    text-align: right;
    color: #6b5b2b;
    letter-spacing: 1px;
    text-transform: uppercase;
    font-size: 10px;
  }

  .toolbar {
    text-align: center;
    margin: 12px auto 0;
  }
  .toolbar button {
    background: #c41e3a;
    color: #fff;
    border: 0;
    padding: 9px 18px;
    font-size: 13px;
    border-radius: 4px;
    cursor: pointer;
    margin: 0 4px;
    font-family: "Helvetica Neue", Arial, sans-serif;
  }
  .toolbar button.secondary { background: #555; }
  @media print {
    body { background: #fff; }
    .certificate { box-shadow: none; margin: 0; border: 2px solid #1a1a1a; width: 100%; height: 100%; }
    .toolbar { display: none; }
  }
</style>
</head>
<body>
  <div class="toolbar">
    <button onclick="window.print()">Print</button>
    <button class="secondary" onclick="window.close()">Close</button>
  </div>
  <div class="certificate">
    <span class="corner tl"></span>
    <span class="corner tr"></span>
    <span class="corner bl"></span>
    <span class="corner br"></span>

    <div class="header">
      <img src="${fullLogoUrl}" alt="Burns Elite Baseball" />
      <p class="academy">Burns Elite Baseball Academy</p>
      <h1 class="doc-title">Player Progress Report</h1>
    </div>

    <div class="player-line">
      <h2 class="player-name">${report.participant_name}</h2>
      <p class="player-meta">
        <strong>${report.camp_name || ""}</strong>
        ${dateRange ? ` &middot; ${dateRange}` : ""}
        ${time ? ` &middot; ${time}` : ""}
        ${location ? ` &middot; ${location}` : ""}
        <br/>Report issued ${sentDate}
      </p>
    </div>

    <div class="stack">
      ${sec("Overall Assessment", report.overall_assessment)}
      ${sec("Skills Progress", report.skills_progress)}
      ${sec("Areas for Improvement", report.areas_for_improvement)}
      ${sec("Coach's Comments", report.coach_comments)}
    </div>

    <div class="footer-row">
      <div>
        <div class="signature-mark">Tom Burns</div>
        <div class="sig-name">Tom Burns</div>
        <div class="sig-role">Founder &amp; Head Coach</div>
      </div>
      <div class="academy-foot">
        Burns Elite Baseball Academy<br/>Miramichi, New Brunswick
      </div>
    </div>
  </div>
  <script>
    // Wait for both the logo and the signature web-font to be ready before
    // opening the print dialog, so the certificate isn't printed with a fallback
    // font or a missing logo.
    (function () {
      var img = document.querySelector('.header img');
      var done = false;
      function go() {
        if (done) return; done = true;
        var fonts = (document.fonts && document.fonts.ready) || Promise.resolve();
        fonts.then(function () {
          setTimeout(function(){ window.print(); }, 250);
        });
      }
      if (!img || img.complete) { go(); return; }
      img.addEventListener('load', go);
      img.addEventListener('error', go);
      setTimeout(go, 3000);   // hard cap so we don't hang forever
    })();
  </script>
</body>
</html>`);
  win.document.close();
}

export default function ProgressReportModal({ open, onOpenChange, report, registration }) {
  const { t } = useLanguage();
  if (!report) return null;

  const handlePrint = () => {
    openCertificatePrintWindow({
      report,
      registration,
      t,
      logoOrigin: window.location.origin,
    });
  };

  const sentDate = new Date(report.sent_at).toLocaleDateString("en-US", {
    year: "numeric", month: "short", day: "numeric",
  });
  const dateRange = formatDateRange(registration?.camp_start_date, registration?.camp_end_date);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3 mb-2">
            <div>
              <DialogTitle className="font-heading text-xl text-burns-gold">
                {t("parent.reportModalTitle")}
              </DialogTitle>
              <p className="text-zinc-400 text-sm mt-1">
                {report.participant_name} {report.camp_name ? `· ${report.camp_name}` : ""}
              </p>
              <p className="text-zinc-500 text-xs mt-0.5">
                {dateRange ? dateRange + " · " : ""}{registration?.camp_time || ""}
                {report.language === "fr" && <span className="ml-2 text-burns-gold uppercase">FR</span>}
              </p>
              <p className="text-zinc-500 text-xs mt-0.5">{t("parent.reportSent")} {sentDate}</p>
            </div>
            <Button
              size="sm"
              onClick={handlePrint}
              className="bg-burns-red hover:bg-burns-red/80 text-white"
              data-testid="print-progress-report"
            >
              <Printer className="w-4 h-4 mr-2" /> {t("parent.print")}
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <p className="text-burns-gold text-xs uppercase tracking-wide mb-1">{t("parent.sectionOverall")}</p>
            <p className="text-zinc-200 text-sm whitespace-pre-wrap">{report.overall_assessment}</p>
          </div>
          <div>
            <p className="text-burns-gold text-xs uppercase tracking-wide mb-1">{t("parent.sectionSkills")}</p>
            <p className="text-zinc-200 text-sm whitespace-pre-wrap">{report.skills_progress}</p>
          </div>
          <div>
            <p className="text-burns-gold text-xs uppercase tracking-wide mb-1">{t("parent.sectionImprove")}</p>
            <p className="text-zinc-200 text-sm whitespace-pre-wrap">{report.areas_for_improvement}</p>
          </div>
          <div>
            <p className="text-burns-gold text-xs uppercase tracking-wide mb-1">{t("parent.sectionComments")}</p>
            <p className="text-zinc-200 text-sm whitespace-pre-wrap">{report.coach_comments}</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
